import { useState } from 'react';

// NsPage: form-page — standalone login-flow form, its input is a real form
// field (email address), not a filter/toolbar control.
export default function ForgotPassword() {
  const cfg     = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  const name    = cfg.companyName  || 'Portal';
  const primary = cfg.primaryColor || '#062722'; // hex-ok — tenant primaryColor fallback, no matching semantic var

  const [email,  setEmail]  = useState('');
  const [status, setStatus] = useState('idle');
  const [error,  setError]  = useState('');

  // DEAD-ON-LOAD-1: TOTP-shortcut path, additive alongside the email-link flow
  // above. 'email' = default flow; 'totp-email' = collecting the email for the
  // shortcut check; 'totp-verify' = a real preAuthToken came back, collecting
  // code + new password; 'totp-unavailable' = the enumeration-safe generic
  // reply came back (account doesn't exist OR has no TOTP — indistinguishable
  // by design, matching the existing email flow's anti-enumeration posture).
  const [mode, setMode] = useState('email');
  const [totpEmail, setTotpEmail] = useState('');
  const [totpChecking, setTotpChecking] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpNewPassword, setTotpNewPassword] = useState('');
  const [totpConfirmPassword, setTotpConfirmPassword] = useState('');
  const [totpSaving, setTotpSaving] = useState(false);
  const [totpError, setTotpError] = useState('');
  const [totpDone, setTotpDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('sending');
    try {
      const res  = await fetch('/api/roch-auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error ?? 'Failed.'); setStatus('error'); }
      else setStatus('sent');
    } catch { setError('Network error. Please try again.'); setStatus('error'); }
  }

  async function handleTotpEmailSubmit(e) {
    e.preventDefault();
    if (!totpEmail.trim()) return;
    setTotpChecking(true);
    setTotpError('');
    try {
      const res  = await fetch('/api/roch-auth/forgot-password-totp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: totpEmail.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setTotpError(data.error ?? 'Request failed.'); return; }
      if (data.totpAvailable && data.preAuthToken) {
        setPreAuthToken(data.preAuthToken);
        setMode('totp-verify');
      } else {
        setMode('totp-unavailable');
      }
    } catch {
      setTotpError('Network error. Please try again.');
    } finally {
      setTotpChecking(false);
    }
  }

  async function handleTotpVerifySubmit(e) {
    e.preventDefault();
    setTotpError('');
    if (!/^\d{6}$/.test(totpCode)) return setTotpError('Enter your 6-digit authenticator code.');
    if (totpNewPassword.length < 8) return setTotpError('Password must be at least 8 characters.');
    if (totpNewPassword !== totpConfirmPassword) return setTotpError('Passwords do not match.');
    setTotpSaving(true);
    try {
      const res  = await fetch('/api/roch-auth/reset-password-totp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preAuthToken, code: totpCode, newPassword: totpNewPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setTotpError(data.error ?? 'Reset failed.'); return; }
      setTotpDone(true);
    } catch {
      setTotpError('Network error. Please try again.');
    } finally {
      setTotpSaving(false);
    }
  }

  const inputStyle = { width: '100%', padding: '0.625rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: '0.375rem', fontSize: '0.9rem', marginBottom: '0.625rem', boxSizing: 'border-box' };
  const buttonStyle = disabled => ({ padding: '0.625rem 1.5rem', background: primary, color: 'var(--color-accent-btn-text)', border: 'none', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', width: '100%', opacity: disabled ? 0.7 : 1 });
  const linkStyle = { display: 'block', marginTop: '1rem', color: primary, fontSize: '0.8125rem', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', width: '100%' };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--color-bg-primary)' }}>
      <div style={{ textAlign: 'center', padding: '2.5rem', background: 'var(--color-bg-secondary)', borderRadius: '0.75rem', boxShadow: '0 4px 24px rgba(6,39,34,0.12)', maxWidth: '360px', width: '100%', border: '1px solid var(--color-border)' }}>
        <div style={{ fontWeight: 600, fontSize: '1.6rem', color: primary, marginBottom: '0.25rem' }}>{name}</div>
        <div style={{ fontSize: '0.875rem', color: 'var(--color-text-label)', marginBottom: '1.75rem' }}>Reset your password</div>

        {mode === 'email' && (
          status === 'sent' ? (
            <p style={{ color: 'var(--color-success-text)', background: 'var(--color-success-bg)', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', margin: 0 }}>
              If that email is registered, a reset link is on its way.
            </p>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com" autoComplete="username" required autoFocus
                  style={inputStyle}
                />
                {status === 'error' && <p style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{error}</p>}
                <button type="submit" disabled={status === 'sending'} style={buttonStyle(status === 'sending')}>
                  {status === 'sending' ? 'Sending…' : 'Email me a reset link'}
                </button>
              </form>
              <button type="button" style={linkStyle} onClick={() => { setMode('totp-email'); setTotpError(''); }}>
                Have an authenticator app? Verify a code instead
              </button>
            </>
          )
        )}

        {mode === 'totp-email' && (
          <form onSubmit={handleTotpEmailSubmit}>
            <input
              type="email" value={totpEmail} onChange={e => setTotpEmail(e.target.value)}
              placeholder="your@email.com" autoComplete="username" required autoFocus
              style={inputStyle}
            />
            {totpError && <p style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{totpError}</p>}
            <button type="submit" disabled={totpChecking} style={buttonStyle(totpChecking)}>
              {totpChecking ? 'Checking…' : 'Continue'}
            </button>
            <button type="button" style={linkStyle} onClick={() => { setMode('email'); setTotpError(''); }}>
              ← Use email link instead
            </button>
          </form>
        )}

        {mode === 'totp-unavailable' && (
          <>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem' }}>
              If that account has an authenticator app enrolled, you can verify a code here. Otherwise, use the email link instead.
            </p>
            <button type="button" style={buttonStyle(false)} onClick={() => { setMode('email'); setStatus('idle'); setEmail(totpEmail); }}>
              Email me a reset link
            </button>
          </>
        )}

        {mode === 'totp-verify' && (
          totpDone ? (
            <>
              <p style={{ color: 'var(--color-success-text)', background: 'var(--color-success-bg)', padding: '0.75rem', borderRadius: '0.375rem', fontSize: '0.875rem', margin: '0 0 1.25rem' }}>
                Password updated. You can now sign in.
              </p>
              <a href="/" style={{ color: primary, fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}>Sign in →</a>
            </>
          ) : (
            <form onSubmit={handleTotpVerifySubmit}>
              <input
                type="text" value={totpCode} onChange={e => setTotpCode(e.target.value)}
                placeholder="6-digit authenticator code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                autoComplete="one-time-code" name="attestation-code" data-lpignore="true" data-1p-ignore
                required autoFocus style={inputStyle}
              />
              <input
                type="password" value={totpNewPassword} onChange={e => setTotpNewPassword(e.target.value)}
                placeholder="New password (min 8 chars)" autoComplete="new-password" required
                style={inputStyle}
              />
              <input
                type="password" value={totpConfirmPassword} onChange={e => setTotpConfirmPassword(e.target.value)}
                placeholder="Confirm new password" autoComplete="new-password" required
                style={inputStyle}
              />
              {totpError && <p style={{ color: 'var(--color-danger)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>{totpError}</p>}
              <button type="submit" disabled={totpSaving} style={buttonStyle(totpSaving)}>
                {totpSaving ? 'Saving…' : 'Verify and set password'}
              </button>
            </form>
          )
        )}

        <a href="/" style={{ display: 'block', marginTop: '1.25rem', color: primary, fontSize: '0.8125rem', textDecoration: 'none' }}>← Back to sign in</a>
      </div>
    </div>
  );
}
